import { Box, Grid, IconButton } from "@material-ui/core";
import MaterialTable from "material-table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoom, deleteRoomById, getAllRoom, updateRoomById } from "../../../../../api/buildingSettingApi";
import { swalCondition } from "../../../../../utils/swalHelper";
import { initialRoomTableOptions, initialRoomValues, roomTableRoom } from "../helpers/AssignBuildEvtOption";
import { Modal, ModalBody } from "react-bootstrap";
import FormRoom from "./FormRoom";
import { Close } from "@material-ui/icons";
import { useSelector } from "react-redux";

export default function AssignBuildRoom({ buildingOption = [], floorOption = [] }) {
    // ref
    const controllerRef = useRef(null);
    const tableRef = useRef(null);

    // global redux state
    const { thisMenuPermission } = useSelector(state => {
        const { menuPermission } = state?.menu;
        return {
            thisMenuPermission: menuPermission?.find(menu => menu?.path === window.location.pathname)?.permission,
        };
    });

    // component state
    const [tableOptions, setTableOptions] = useState(initialRoomTableOptions);
    const [isShowModal, setIsShowModal] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState(initialRoomValues);

    // fetch data
    const fetchData = useCallback(async query => {
        try {
            const queryParams = {
                page: query?.page + 1 || 1,
                limit: query?.pageSize || 10,
                search: query?.search || "",
                order: query?.orderBy?.field || "floorId",
                direction: query?.orderBy?.field ? query?.orderDirection : "desc",
            };
            const res = await getAllRoom(queryParams, controllerRef.current?.signal);
            if (res) {
                setTableOptions(currentState => ({
                    ...currentState,
                    currentPage: query?.page,
                    pageSize: query?.pageSize || 10,
                    totalData: res?.pagination?.totalData || 0,
                    order: query?.orderBy?.field || "floorId",
                    direction: query?.orderBy?.field ? query?.orderDirection : "desc",
                }));
                return {
                    data: res?.data || [],
                    page: query?.page || 0,
                    totalCount: res?.pagination?.totalData || 0,
                };
            }
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        } catch (error) {
            console.dir(error);
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
    }, []);

    // add room
    const handleAddRoom = useCallback(async (values, { resetForm }) => {
        try {
            const isCreate = await createRoom(
                {
                    name: values?.roomName,
                    nameEn: values?.roomNameEnglish,
                    buildingId: values?.buildingId,
                    floorId: values?.floorId,
                    status: values?.status,
                },
                controllerRef.current?.signal,
            );
            if (isCreate) {
                resetForm();
                tableRef.current.onQueryChange();
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // edit room
    const handleEditRoom = useCallback(async values => {
        try {
            const isUpdate = await updateRoomById(
                values?.roomId,
                {
                    name: values?.roomName,
                    nameEn: values?.roomNameEnglish,
                    buildingId: values?.buildingId,
                    floorId: values?.floorId,
                    status: values?.status,
                },
                controllerRef.current?.signal,
            );
            if (isUpdate) {
                setIsShowModal(false);
                tableRef.current.onQueryChange();
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // delete room
    const handleDeleteRoom = useCallback(async room => {
        try {
            const isConfirm = await swalCondition('<div className="h3 t-text-primary">Are you delete?</div>', "", {
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "YES",
                cancelButtonText: "NO",
            });
            if (isConfirm) {
                const isDelete = await deleteRoomById(room?.roomId, controllerRef.current?.signal);
                if (isDelete) {
                    tableRef.current.onQueryChange();
                }
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // open edit form modal
    const handleOpenEditFormModal = useCallback(async room => {
        setInitialFormValues({
            roomId: room?.roomId,
            buildingId: room?.Floor?.buildingId,
            floorId: room?.floorId,
            status: room?.status,
            roomName: room?.name,
            roomNameEnglish: room?.nameEn,
        });
        setIsShowModal(true);
    }, []);

    // close modal
    const handleCloseModal = useCallback(async () => {
        setIsShowModal(false);
        setInitialFormValues(initialRoomValues);
    }, []);

    // run only first render
    useEffect(() => {
        controllerRef.current = new AbortController();
        // to avoid memory leaked.
        return () => {
            controllerRef.current.abort();
        };
    }, []);

    return (
        <Grid container spacing={0} className={"py-10"}>
            {/* form add */}
            {thisMenuPermission?.create && (
                <Grid item xs={12} className="mb-10">
                    <FormRoom
                        onSubmit={handleAddRoom}
                        initialFormValues={initialFormValues}
                        isCreateForm
                        buildingOption={buildingOption}
                        floorOption={floorOption}
                    />
                </Grid>
            )}

            {/* table */}
            <Grid item xs={12}>
                <div className="t-table-custom">
                    <MaterialTable
                        tableRef={tableRef}
                        components={{ Container: Box }}
                        title=""
                        data={fetchData}
                        options={tableOptions}
                        columns={roomTableRoom}
                        actions={[
                            {
                                icon: "edit",
                                iconProps: { style: { fontSize: 18 }, className: "action-edit" },
                                tooltip: "Edit",
                                onClick: (event, rowData) => handleOpenEditFormModal(rowData),
                                hidden: !thisMenuPermission?.edit,
                            },
                            {
                                icon: "delete",
                                iconProps: { style: { fontSize: 18 }, className: "action-delete" },
                                tooltip: "Delete",
                                onClick: (event, rowData) => handleDeleteRoom(rowData),
                                hidden: !thisMenuPermission?.delete,
                            },
                        ]}
                    />
                </div>
            </Grid>

            {/* modal */}
            <Grid item xs={12}>
                <Modal show={isShowModal} size="md" centered={true} scrollable={true} onHide={handleCloseModal}>
                    <Modal.Header>
                        <Modal.Title>Edit Building</Modal.Title>
                        <IconButton onClick={handleCloseModal} size="small">
                            <Close />
                        </IconButton>
                    </Modal.Header>
                    <ModalBody>
                        <FormRoom
                            onSubmit={handleEditRoom}
                            initialFormValues={initialFormValues}
                            isCreateForm={false}
                            buildingOption={buildingOption}
                            floorOption={floorOption}
                        />
                    </ModalBody>
                </Modal>
            </Grid>
        </Grid>
    );
}
